/** Imports ---------------------------------------------------------------- */

/** React imports */
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";

/** MUI imports */
import { styled } from "@mui/material/styles";
import { Box, Chip, Grid, Typography, Divider, FormControlLabel, Checkbox } from "@mui/material";

/** Component imports */
import { Onboarding, CatchUp, Recurring, Consulting } from "./";

/** Component styles */
const PREFIX = "ControllerRoot";

const classes = {
    cardContentRoot: `${PREFIX}-cardContentRoot`,
    chip: `${PREFIX}-chip`,
    chipProActive: `${PREFIX}-chipProActive`,
    chipProWasActive: `${PREFIX}-chipProWasActive`,
    selectedChip: `${PREFIX}-selectedChip`,
    selectedChipActive: `${PREFIX}-selectedChipActive`,
    selectedChipWasActive: `${PREFIX}-selectedChipWasActive`,
    divider: `${PREFIX}-divider`,
};

const StyledContainer = styled(Box)(({ theme }) => ({
    [`& .${classes.cardContentRoot}`]: {
        padding: "24px 48px 48px",
    },

    [`& .${classes.chip}`]: {
        padding: 20,
        width: 200,
        backgroundColor: "#EAEAF4",
        color: "#031851",
        fontWeight: "700 !important",
        "&:hover": {
            backgroundColor: "rgba(213, 214, 233, 0.3)",
            color: "#031851",
        },
        borderRadius: 40,
    },

    [`& .${classes.chipProActive}`]: {
        padding: 20,
        width: 200,
        backgroundColor: "#EAEAF4",
        color: "#031851",
        border: "2px solid #ff5968",
        fontWeight: "700 !important",
        "&:hover": {
            backgroundColor: "rgba(213, 214, 233, 0.3)",
            color: "#031851",
        },
        borderRadius: 40,
    },

    [`& .${classes.chipProWasActive}`]: {
        padding: 20,
        width: 200,
        backgroundColor: "#EAEAF4",
        color: "#031851",
        border: "2px solid #031851",
        fontWeight: "700 !important",
        "&:hover": {
            backgroundColor: "rgba(213, 214, 233, 0.3)",
            color: "#031851",
        },
        borderRadius: 40,
    },

    [`& .${classes.selectedChip}`]: {
        padding: 20,
        width: 200,
        backgroundColor: "#2f3190",
        color: "#fff",
        fontWeight: "700 !important",
        borderRadius: 40,
        "&:hover": {
            backgroundColor: "rgba(47, 49, 144, 0.9)",
            color: "#fff",
        },
    },

    [`& .${classes.selectedChipActive}`]: {
        padding: 20,
        width: 200,
        backgroundColor: "#2f3190",
        color: "#fff",
        fontWeight: "700 !important",
        border: "2px solid #ff5968",
        borderRadius: 40,
        "&:hover": {
            backgroundColor: "rgba(47, 49, 144, 0.9)",
            color: "#fff",
        },
    },

    [`& .${classes.selectedChipWasActive}`]: {
        padding: 20,
        width: 200,
        backgroundColor: "#2f3190",
        color: "#fff",
        fontWeight: "700 !important",
        border: "2px solid #031851",
        borderRadius: 40,
        "&:hover": {
            backgroundColor: "rgba(47, 49, 144, 0.9)",
            color: "#fff",
        },
    },

    [`& .${classes.divider}`]: {
        marginBottom: 10,
    },
}));

export const ControllerRoot = (props) => {
    // Properties
    const {
        setTexto,
        setalertType,
        setShow,
        setBackDrop,
        clientMarket,
        expenseEst,
        controlSystem,
        serviceData,
        companyID,
        setServiceData,
        setAuxExpanded,
        setExpanded,
        lastChipSelected,
        setLastChipSelected,
    } = props;

    /** Internationalization i18n */
    const [t] = useTranslation("global");
    const [renderState, setRenderState] = useState(false);
    
    /* Component states */
    const [valuesAcc, setValuesAcc] = useState({
        controlOnboard: false,
        controlCatchUp: false,
        controllerRecu: false,
        controlConsul: false,
    });
    const [projectsData, setProjectsData] = useState({
        controlOnboard: {},
        controlCatchUp: {},
        controllerRecu: {},
        controlConsul: {},
    });
    const [active, setActive] = useState({
        controlOnboard: {
            isActive: false,
            wasActive: false,
        },
        controlCatchUp: {
            isActive: false,
            wasActive: false,
        },
        controllerRecu: {
            isActive: false,
            wasActive: false,
        },
        controlConsul: {
            isActive: false,
            wasActive: false,
        },
    });
    const [open, setOpen] = useState(false);
    const [infoAdd, setInfoAdd] = useState({});
    const [checkRemind, setCheckRemind] = useState(false);

    const handleCloseDialog = () => {
        setInfoAdd({});
        setCheckRemind(false);
        setOpen(false);
    };

    const showConfirm = (value) => {
        setInfoAdd(value);
        setOpen(true);
    };

    const handleChangeCheck = (event) => {
        setCheckRemind(event.target.checked);
    };

    const bodyMess = (
        <>
            <Typography variant="h1" color="primary" align="center" sx={{ fontSize: 18, pt: 3, pb: 2 }}>
                {t("miscellaneous.confRemoveCheckDet")}
            </Typography>
            <FormControlLabel
                control={
                    <Checkbox
                        name="checkedConf"
                        checked={checkRemind}
                        onChange={handleChangeCheck}
                        inputProps={{
                            "aria-label": "controlled",
                        }}
                    />
                }
                label={t("miscellaneous.dontShowAgain")}
            />
        </>
    );

    const getProjects = async (isReload) => {
        setBackDrop(true);

        let serviceCompID = serviceData._id ?? "";

        if (isReload) {
            await fetch(`/getNewServicesCompany?company_id=${companyID}`, {
                method: "GET",
                headers: {
                    "Content-type": "application/json; charset=UTF-8",
                    "Access-Control-Allow-Origin": "*",
                },
            })
                .then((res) => res.json())
                .then(async(data) => {
                    setServiceData(data);

                    let lastProject = data.filter(function (o) {
                        return o.hasOwnProperty("_id") && o.service_name === "Controller";
                    })[0];

                    await fetch(
                        `/getProjectsCompany?company_id=${lastProject.company_id}&service_id=${lastProject.service_id}&service_company_id=${lastProject._id}`,
                        {
                            method: "GET",
                            headers: {
                                "Content-type": "application/json; charset=UTF-8",
                                "Access-Control-Allow-Origin": "*",
                            },
                        }
                    )
                        .then((res) => res.json())
                        .then(async (data) => {
                            let result = {};
                            let lastProyects = {
                                catchUp: {},
                                recurring: {},
                                consulting: {},
                            };
                            let recurringisActive = false;
                            let wasRecurringActive = false;
        
                            await data.forEach((item) => {
                                switch (item.project_type) {
                                    case 7:
                                        result.controlOnboard = item;
                                        break;
                                    case 8:
                                        result.controllerRecu = item;
                                        if (item.projects_array.length !== 0) {
                                            let indexExact = 0;
                                            let dateMax = null;
                                            item.projects_array.forEach((item, index) => {
                                                if (index === 0) {
                                                    dateMax = item.end_date;
                                                } else {
                                                    if (moment(dateMax).isBefore(item.end_date)) {
                                                        dateMax = item.end_date;
                                                        indexExact = index;
                                                    }
                                                }
                                                if (!item.client_delivery_date && item.client_commitment_date) {
                                                    recurringisActive = true;
                                                } else if (item.client_delivery_date) {
                                                    wasRecurringActive = moment(item.client_delivery_date).isSameOrBefore(
                                                        moment()
                                                    );
                                                }
                                            });
                                            lastProyects.recurring = item.projects_array[indexExact];
                                            result.controllerRecu.project_company_id =
                                                item.projects_array[0].project_company_id;
                                        }
                                        break;
                                    case 9:
                                        result.controlCatchUp = item;
                                        if (item.projects_array.length !== 0) {
                                            lastProyects.catchUp = item.projects_array[0];
                                            result.controlCatchUp.project_company_id =
                                                item.projects_array[0].project_company_id;
                                        }
                                        break;
                                    case 10:
                                        result.controlConsul = item;
                                        if (item.projects_array.length !== 0) {
                                            lastProyects.consulting = item.projects_array[0];
                                            result.controlConsul.project_company_id = item.projects_array[0].project_company_id;
                                        }
                                        break;
                                    default:
                                        break;
                                }
                            });
        
                            setProjectsData({
                                controlOnboard: result.controlOnboard,
                                controlCatchUp: result.controlCatchUp,
                                controllerRecu: result.controllerRecu,
                                controlConsul: result.controlConsul,
                            });
        
                            setActive({
                                controlOnboard: {
                                    isActive:
                                        result.controlOnboard.since_date &&
                                        (!result.controlOnboard.end_date ||
                                            moment(result.controlOnboard.end_date).isAfter(moment())),
                                    wasActive: result.controlOnboard.end_date
                                        ? moment(result.controlOnboard.end_date).isSameOrBefore(moment())
                                        : false,
                                },
                                controlCatchUp: {
                                    isActive: lastProyects.catchUp.since_date && !lastProyects.catchUp.client_delivery_date,
                                    wasActive: lastProyects.catchUp.client_delivery_date
                                        ? moment(lastProyects.catchUp.client_delivery_date).isSameOrBefore(moment())
                                        : false,
                                },
                                controllerRecu: {
                                    isActive: recurringisActive,
                                    wasActive: wasRecurringActive,
                                },
                                controlConsul: {
                                    isActive: false,
                                    wasActive: lastProyects.consulting.since_date ? true : false,
                                },
                            });

                            setAuxExpanded(lastProject.service_name);
                            setExpanded(true);
                            setRenderState(!renderState)
                        });
                });
        } else {
            await fetch(
                `/getProjectsCompany?company_id=${companyID}&service_id=${serviceData.service_id}&service_company_id=${serviceCompID}`,
                {
                    method: "GET",
                    headers: {
                        "Content-type": "application/json; charset=UTF-8",
                        "Access-Control-Allow-Origin": "*",
                    },
                }
            )
                .then((res) => res.json())
                .then((data) => {
                    let result = {};
                    let lastProyects = {
                        catchUp: {},
                        recurring: {},
                        consulting: {},
                    };
                    let recurringisActive = false;
                    let wasRecurringActive = false;

                    data.forEach((item) => {
                        switch (item.project_type) {
                            case 7:
                                result.controlOnboard = item;
                                break;
                            case 8:
                                result.controllerRecu = item;
                                if (item.projects_array.length !== 0) {
                                    let indexExact = 0;
                                    let dateMax = null;
                                    item.projects_array.forEach((item, index) => {
                                        if (index === 0) {
                                            dateMax = item.end_date;
                                        } else {
                                            if (moment(dateMax).isBefore(item.end_date)) {
                                                dateMax = item.end_date;
                                                indexExact = index;
                                            }
                                        }
                                        if (item.since_date && !item.client_delivery_date) {
                                            recurringisActive = true;
                                        } else if (item.client_delivery_date) {
                                            wasRecurringActive = moment(item.client_delivery_date).isSameOrBefore(
                                                moment()
                                            );
                                        }
                                    });
                                    lastProyects.recurring = item.projects_array[indexExact];
                                    result.controllerRecu.project_company_id =
                                        item.projects_array[0].project_company_id;
                                }
                                break;
                            case 9:
                                result.controlCatchUp = item;
                                if (item.projects_array.length !== 0) {
                                    lastProyects.catchUp = item.projects_array[0];
                                    result.controlCatchUp.project_company_id =
                                        item.projects_array[0].project_company_id;
                                }
                                break;
                            case 10:
                                result.controlConsul = item;
                                if (item.projects_array.length !== 0) {
                                    lastProyects.consulting = item.projects_array[0];
                                    result.controlConsul.project_company_id = item.projects_array[0].project_company_id;
                                }
                                break;
                            default:
                                break;
                        }
                    });

                    setProjectsData({
                        controlOnboard: result.controlOnboard,
                        controlCatchUp: result.controlCatchUp,
                        controllerRecu: result.controllerRecu,
                        controlConsul: result.controlConsul,
                    });

                    setActive({
                        controlOnboard: {
                            isActive:
                                result.controlOnboard.since_date &&
                                (!result.controlOnboard.end_date ||
                                    moment(result.controlOnboard.end_date).isAfter(moment())),
                            wasActive: result.controlOnboard.end_date
                                ? moment(result.controlOnboard.end_date).isSameOrBefore(moment())
                                : false,
                        },
                        controlCatchUp: {
                            isActive: lastProyects.catchUp.since_date && !lastProyects.catchUp.client_delivery_date,
                            wasActive: lastProyects.catchUp.client_delivery_date
                                ? moment(lastProyects.catchUp.client_delivery_date).isSameOrBefore(moment())
                                : false,
                        },
                        controllerRecu: {
                            isActive: recurringisActive,
                            wasActive: wasRecurringActive,
                        },
                        controlConsul: {
                            isActive: false,
                            wasActive: lastProyects.consulting.since_date ? true : false,
                        },
                    });
                });
        }

        setBackDrop(false);
    };

    /* Component functions */
    useEffect(() => {
        getProjects(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* Component functions */
    useEffect(() => {
        if (lastChipSelected){
            handleClickChip(valuesAcc[lastChipSelected], lastChipSelected, false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [renderState]);

    const handleClickChip = async (value, option, setChip = false) => {
        switch (option) {
            case "controlOnboard":
                setValuesAcc({
                    ...valuesAcc,
                    controlOnboard: setChip ? !value : true,
                    controlCatchUp: false,
                    controllerRecu: false,
                    controlConsul: false,
                });
                break;
            case "controlCatchUp":
                setValuesAcc({
                    ...valuesAcc,
                    controlCatchUp: setChip ? !value : true,
                    controlOnboard: false,
                    controllerRecu: false,
                    controlConsul: false,
                });
                break;
            case "controllerRecu":
                setValuesAcc({
                    ...valuesAcc,
                    controllerRecu: setChip ? !value : true,
                    controlCatchUp: false,
                    controlOnboard: false,
                    controlConsul: false,
                });
                break;
            default:
                setValuesAcc({
                    ...valuesAcc,
                    controlConsul: setChip ? !value : true,
                    controllerRecu: false,
                    controlCatchUp: false,
                    controlOnboard: false,
                });
                break;
        }
        if (setChip) {
            setLastChipSelected(option);
        }
    };

    return (
        <StyledContainer>
            <Divider className={classes.divider} />
            <Grid container justifyContent="space-around" alignItems="center" spacing={2} sx={{ pb: 2 }}>
                <Grid item>
                    <Chip
                        id="onboarding-chip"
                        label={
                            <Typography variant="h2" align="center" sx={{ fontWeight: 700 }}>
                                {t(`invoicing.onboarding`)}
                            </Typography>
                        }
                        className={
                            valuesAcc.controlOnboard
                                ? active.controlOnboard.isActive
                                    ? classes.selectedChipActive
                                    : active.controlOnboard.wasActive
                                    ? classes.selectedChipWasActive
                                    : classes.selectedChip
                                : active.controlOnboard.isActive
                                ? classes.chipProActive
                                : active.controlOnboard.wasActive
                                ? classes.chipProWasActive
                                : classes.chip
                        }
                        onClick={() => handleClickChip(valuesAcc.controlOnboard, "controlOnboard", true)}
                    />
                </Grid>
                <Grid item>
                    <Chip
                        id="catch-up-chip"
                        label={
                            <Typography variant="h2" align="center" sx={{ fontWeight: 700 }}>
                                {t(`invoicing.catchUp`)}
                            </Typography>
                        }
                        className={
                            valuesAcc.controlCatchUp
                                ? active.controlCatchUp.isActive
                                    ? classes.selectedChipActive
                                    : active.controlCatchUp.wasActive
                                    ? classes.selectedChipWasActive
                                    : classes.selectedChip
                                : active.controlCatchUp.isActive
                                ? classes.chipProActive
                                : active.controlCatchUp.wasActive
                                ? classes.chipProWasActive
                                : classes.chip
                        }
                        onClick={() => handleClickChip(valuesAcc.controlCatchUp, "controlCatchUp", true)}
                    />
                </Grid>
                <Grid item>
                    <Chip
                        id="recurring-chip"
                        label={
                            <Typography variant="h2" align="center" sx={{ fontWeight: 700 }}>
                                {t(`invoicing.recurring`)}
                            </Typography>
                        }
                        className={
                            valuesAcc.controllerRecu
                                ? active.controllerRecu.isActive
                                    ? classes.selectedChipActive
                                    : active.controllerRecu.wasActive
                                    ? classes.selectedChipWasActive
                                    : classes.selectedChip
                                : active.controllerRecu.isActive
                                ? classes.chipProActive
                                : active.controllerRecu.wasActive
                                ? classes.chipProWasActive
                                : classes.chip
                        }
                        onClick={() => handleClickChip(valuesAcc.controllerRecu, "controllerRecu", true)}
                    />
                </Grid>
                <Grid item>
                    <Chip
                        id="consulting-chip"
                        label={
                            <Typography variant="h2" align="center" sx={{ fontWeight: 700 }}>
                                {t(`invoicing.consulting`)}
                            </Typography>
                        }
                        className={
                            valuesAcc.controlConsul
                                ? active.controlConsul.isActive
                                    ? classes.selectedChipActive
                                    : active.controlConsul.wasActive
                                    ? classes.selectedChipWasActive
                                    : classes.selectedChip
                                : active.controlConsul.isActive
                                ? classes.chipProActive
                                : active.controlConsul.wasActive
                                ? classes.chipProWasActive
                                : classes.chip
                        }
                        onClick={() => handleClickChip(valuesAcc.controlConsul, "controlConsul", true)}
                    />
                </Grid>
            </Grid>
            {valuesAcc.controlOnboard && (
                <Onboarding
                    setTexto={setTexto}
                    setalertType={setalertType}
                    setShow={setShow}
                    companyID={companyID}
                    reloadProjects={getProjects}
                    setBackDrop={setBackDrop}
                    data={projectsData.controlOnboard}
                    serviceID={serviceData.service_id}
                    controlSystem={controlSystem}
                    expenseEst={expenseEst}
                />
            )}
            {valuesAcc.controlCatchUp && (
                <CatchUp
                    setTexto={setTexto}
                    setalertType={setalertType}
                    setShow={setShow}
                    clientMarket={clientMarket}
                    companyID={companyID}
                    expenseEst={expenseEst}
                    controlSystem={controlSystem}
                    reloadProjects={getProjects}
                    setBackDrop={setBackDrop}
                    data={projectsData.controlCatchUp}
                    serviceID={serviceData.service_id}
                    handleCloseDialog={handleCloseDialog}
                    infoAdd={infoAdd}
                    checkRemind={checkRemind}
                    open={open}
                    bodyMess={bodyMess}
                    showConfirm={showConfirm}
                />
            )}
            {valuesAcc.controllerRecu && (
                <Recurring
                    setTexto={setTexto}
                    setalertType={setalertType}
                    setShow={setShow}
                    setBackDrop={setBackDrop}
                    clientMarket={clientMarket}
                    companyID={companyID}
                    expenseEst={expenseEst}
                    controlSystem={controlSystem}
                    data={projectsData.controllerRecu}
                    reloadProjects={getProjects}
                    serviceID={serviceData.service_id}
                    handleCloseDialog={handleCloseDialog}
                    infoAdd={infoAdd}
                    checkRemind={checkRemind}
                    open={open}
                    bodyMess={bodyMess}
                    showConfirm={showConfirm}
                />
            )}
            {valuesAcc.controlConsul && (
                <Consulting
                    setTexto={setTexto}
                    setalertType={setalertType}
                    setShow={setShow}
                    companyID={companyID}
                    reloadProjects={getProjects}
                    setBackDrop={setBackDrop}
                    data={projectsData.controlConsul}
                    serviceID={serviceData.service_id}
                />
            )}
        </StyledContainer>
    );
};
